/* A50-COLORWAY-GALLERY — custom Authenticity50 feature (isolated file).
 *
 * Owns the <a50-colorway-gallery> element: swaps the photos when the shopper
 * picks another color, drives the arrows, and runs the color chips. Vanilla
 * ES module, no imports, no dependencies.
 * Spec: A50-COLORWAY-GALLERY-IMPLEMENTATION.md §7 and §8.
 *
 * ── Where the color comes from ─────────────────────────────────────────────
 * Impact's documented `variant:change` event. VariantPicker dispatches it ON
 * THE PRODUCT <form>, bubbling (assets/theme.js, selectCombination), after
 * product:rerender has already replaced the picker — so the checked radios are
 * current when it arrives.
 *
 * Quick-buy drawers and a featured-product section fire it too, from their own
 * forms. The gallery follows ONLY the main product form: the one named by
 * `variant-picker[update-url]`'s form-id (only main-product passes
 * update_url). The picker is looked up fresh on every event, because Impact
 * replaces it on every change; the <form> itself persists.
 *
 * Never use `event.detail.formId`: it is not an id. Impact builds it from
 * `form.id`, and the product form contains <input name="id">, so it is that
 * input element. Forms are compared as elements instead.
 *
 * ── The color chips (step 2) ───────────────────────────────────────────────
 * A chip never changes the photos itself. It clicks Impact's own radio for the
 * same option value in the main form, which runs Impact's normal flow (price,
 * availability, add-to-cart, URL) and ends in the same `variant:change` as a
 * tap on the product's own swatches. So the photos can never show a color that
 * add-to-cart wouldn't buy. The chips then re-sync on every change from the
 * main form, whichever control made it.
 *
 * ── Progressive enhancement ────────────────────────────────────────────────
 * Liquid renders the right gallery for the selection at page load. Without
 * this file that gallery simply stays, and the arrows stay hidden: they show
 * only once this file sets data-scrollable. Nothing here touches the product
 * form, add-to-cart or navigation, and every entry point catches its errors.
 *
 * ── No user-facing strings live here ───────────────────────────────────────
 * Every label comes from Liquid (locale keys and section settings).
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const FADE_MS = 200;
const DEFAULT_KEY = '_default';
// Sub-pixel scroll positions never land exactly on 0 or on the maximum.
const EDGE_TOLERANCE = 1;

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const report = (error) => {
  // A failure leaves the server-rendered gallery exactly as it is.
  console.error('A50 colorway gallery:', error);
};

class A50ColorwayGallery extends HTMLElement {
  connectedCallback() {
    // Guard against a double upgrade (the element defined twice, or the editor
    // re-inserting a live node rather than re-rendering it).
    if (this._initialised) return;
    this._initialised = true;

    try {
      this._init();
    } catch (error) {
      report(error);
    }
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._resizeObserver?.disconnect();
    if (this._frame) cancelAnimationFrame(this._frame);
    this._frame = 0;
    this._animation?.cancel();
    this._initialised = false;
  }

  _init() {
    this._track = this.querySelector('[data-a50-cg-track]');
    this._data = this._readData();
    if (!this._track || !this._data) return;

    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);

    // Which gallery is on screen, and the slides of galleries shown before
    // (detached but kept, so returning to a color reuses images that already
    // downloaded). Both survive a disconnect/reconnect of this same element:
    // after swaps the track no longer holds the initial gallery, so resetting
    // the key would park those slides under the wrong color. A theme-editor
    // re-render builds a new element, which starts clean.
    if (this._currentKey === undefined) {
      this._currentKey = typeof this._data.initialKey === 'string' ? this._data.initialKey : '';
    }
    this._cache ??= new Map();

    this._templates = new Map();
    this.querySelectorAll('template[data-a50-cg-key]').forEach((template) => {
      this._templates.set(template.dataset.a50CgKey, template);
    });

    // Every placement's buttons (above, on and below the photos); CSS shows one.
    this._prevButtons = Array.from(this.querySelectorAll('[data-a50-cg-prev]'));
    this._nextButtons = Array.from(this.querySelectorAll('[data-a50-cg-next]'));
    this._bindArrows(this._prevButtons, -1, signal);
    this._bindArrows(this._nextButtons, 1, signal);

    this._track.addEventListener('scroll', () => this._scheduleUpdate(), { passive: true, signal });

    // Resizing changes the slide width and whether anything overflows.
    if ('ResizeObserver' in window) {
      this._resizeObserver = new ResizeObserver(() => this._scheduleUpdate());
      this._resizeObserver.observe(this._track);
    }

    document.addEventListener('variant:change', (event) => this._onVariantChange(event), { signal });

    // Rendered hidden; shown once the main picker is found. The sync also
    // undoes a browser restoring a stale chip on back/forward or reload.
    this._chips = this.querySelector('[data-a50-cg-chips]');
    if (this._chips) {
      this._chips.addEventListener('change', (event) => this._onChipChange(event), { signal });
      this._refreshChips();
      this._syncChips(this._currentKey);
    }

    this._updateArrows();
  }

  _readData() {
    const script = this.querySelector('script[data-a50-cg-data]');
    if (!script) return null;

    try {
      const data = JSON.parse(script.textContent);
      return data && typeof data === 'object' ? data : null;
    } catch (error) {
      report(error);
      return null;
    }
  }

  /* ── Following the color ───────────────────────────────────────────────── */

  _mainForm() {
    const picker = document.querySelector('variant-picker[update-url]');
    const formId = picker?.getAttribute('form-id');
    if (!formId) return null;

    // The same lookup Impact's VariantPicker uses for its own form.
    const form = document.forms[formId];
    return form instanceof HTMLFormElement ? form : null;
  }

  _onVariantChange(event) {
    try {
      const form = this._mainForm();
      if (!form || event.target !== form) return;

      const key = this._keyFor(form, event.detail);
      this._show(key);

      // Every change, even a size-only one: Impact has just rebuilt its
      // picker, and the chips follow whichever control made the change.
      this._refreshChips();
      this._syncChips(key);
    } catch (error) {
      report(error);
    }
  }

  /* Which gallery the main form now asks for (spec §7.3), first match wins:
   * 1. the checked option values, in option order. This also covers a
   *    size/color combination with no variant, where detail.variant is null;
   * 2. the variant map, should Impact's option inputs ever change shape;
   * 3. the default gallery;
   * 4. nothing, and the section hides. */
  _keyFor(form, detail) {
    const optionValueKeys = this._data.optionValueKeys || {};
    const checked = Array.from(form.elements)
      .filter((element) => element.matches('input[data-option-position]:checked'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));

    for (const input of checked) {
      if (hasOwn(optionValueKeys, input.value)) return optionValueKeys[input.value];
    }

    const variantKeys = this._data.variantKeys || {};
    const variantId = detail?.variant?.id;
    if (variantId != null && hasOwn(variantKeys, String(variantId))) {
      return variantKeys[String(variantId)];
    }

    return this._data.hasDefault ? DEFAULT_KEY : '';
  }

  _show(key) {
    // A size-only change: the same photos stay, scroll position and all.
    if (key === this._currentKey) return;

    const track = this._track;

    // 1. Park the current slides.
    if (track.firstChild) {
      const parked = document.createDocumentFragment();
      while (track.firstChild) parked.appendChild(track.firstChild);
      this._cache.set(this._currentKey, parked);
    }

    // 2. Bring in the new ones: parked earlier, or a first copy of the template.
    let incoming = this._cache.get(key) ?? null;
    if (incoming) {
      this._cache.delete(key);
    } else if (this._templates.has(key)) {
      incoming = document.importNode(this._templates.get(key).content, true);
    }
    if (incoming) track.appendChild(incoming);
    this._currentKey = key;

    // 3. Start at the first photo, with no animation. The track sets no
    // scroll-behavior, so this is instant.
    track.scrollLeft = 0;

    // 4. Label, empty state, arrows.
    this._syncLabel();
    this.toggleAttribute('data-empty', !track.querySelector('[data-a50-cg-slide]'));
    this._updateArrows();

    // 5. A short fade, unless the shopper asked for less motion.
    this._fadeIn();
  }

  /* "Shown in <color>" — for a color's own gallery only, never the default. */
  _syncLabel() {
    const label = this.querySelector('[data-a50-cg-shown-in]');
    if (!label) return;

    const names = this._data.names || {};
    const key = this._currentKey;
    const name = key && key !== DEFAULT_KEY && hasOwn(names, key) ? names[key] : '';

    const nameElement = label.querySelector('[data-a50-cg-name]');
    if (name && nameElement) nameElement.textContent = name;
    label.hidden = !name;
  }

  _fadeIn() {
    if (this._reducedMotion.matches || typeof this._track.animate !== 'function') return;

    this._animation?.cancel();
    this._animation = this._track.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: FADE_MS,
      easing: 'ease-out'
    });
  }

  /* ── Color chips ───────────────────────────────────────────────────────── */

  /* Impact's radio for an option value in the main form, or null. Looked up
   * fresh every time: product:rerender replaces the whole picker on each
   * change. form.elements includes the radios that join the form through
   * their `form` attribute, the same list Impact reads the selection from. */
  _impactRadio(form, valueId) {
    return (
      Array.from(form.elements).find(
        (element) => element.matches('input[data-option-position]') && element.value === valueId
      ) ?? null
    );
  }

  /* A chip shows only while the main picker has a radio it can press for its
   * color (spec §8.4); the row shows once at least one chip can. A radio is
   * disabled only under "Hide sold out variants", and which ones depends on
   * the other options' selection, so this re-runs after every change. */
  _refreshChips() {
    if (!this._chips) return;

    const form = this._mainForm();
    let usable = 0;

    this._chips.querySelectorAll('input[data-a50-cg-chip]').forEach((chip) => {
      const radio = form ? this._impactRadio(form, chip.value) : null;
      const ok = Boolean(radio) && !radio.disabled;

      // Disabled as well as hidden, so arrow keys never land on it.
      chip.disabled = !ok;
      const item = chip.closest('[data-a50-cg-chip-item]');
      if (item) item.hidden = !ok;
      if (ok) usable += 1;
    });

    this._chips.hidden = usable === 0;
  }

  /* Check the chip for the gallery on screen. The default gallery and the
   * empty state have no chip, so nothing is checked then. */
  _syncChips(key) {
    if (!this._chips) return;

    const optionValueKeys = this._data.optionValueKeys || {};
    const own = Boolean(key) && key !== DEFAULT_KEY;

    this._chips.querySelectorAll('input[data-a50-cg-chip]').forEach((chip) => {
      const checked = own && hasOwn(optionValueKeys, chip.value) && optionValueKeys[chip.value] === key;
      if (chip.checked !== checked) chip.checked = checked;
    });
  }

  _onChipChange(event) {
    const chip = event.target;
    if (!(chip instanceof HTMLInputElement) || !chip.matches('[data-a50-cg-chip]') || !chip.checked) return;

    try {
      const form = this._mainForm();
      const radio = form ? this._impactRadio(form, chip.value) : null;

      if (!radio || radio.disabled) {
        // The picker no longer offers this color. Hide its chip, and put the
        // selection back on the photos that are showing.
        this._refreshChips();
        this._syncChips(this._currentKey);
        return;
      }

      // Impact takes it from here, and its variant:change swaps the photos
      // and re-syncs the chips. An already-checked radio means that change is
      // already on its way.
      if (!radio.checked) radio.click();
    } catch (error) {
      report(error);
    }
  }

  /* ── Arrows ────────────────────────────────────────────────────────────── */

  _bindArrows(buttons, direction, signal) {
    buttons.forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          // aria-disabled, not `disabled`: the button keeps keyboard focus at
          // the end of the track, so the click has to be ignored here.
          if (button.getAttribute('aria-disabled') === 'true') return;

          try {
            this._scrollByOne(direction);
          } catch (error) {
            report(error);
          }
        },
        { signal }
      );
    });
  }

  /* One press moves one photo: to the start of the next photo past the snap
   * line (or the last one before it). Measured per slide rather than by one
   * slide's width, because with a Photo height set the widths differ
   * (spec §22). The snap line is the track's start edge plus its
   * scroll-padding, where scroll snapping lines photos up. */
  _scrollByOne(direction) {
    const track = this._track;
    const slides = track.querySelectorAll('[data-a50-cg-slide]');
    if (!slides.length) return;

    const style = getComputedStyle(track);
    // In a right-to-left language the start edge is the right one, and "next"
    // runs toward negative scrollLeft.
    const rtl = style.direction === 'rtl';
    const sign = rtl ? -1 : 1;
    const padding = parseFloat(style.scrollPaddingInlineStart) || 0;
    const box = track.getBoundingClientRect();
    const snapLine = rtl ? box.right - padding : box.left + padding;

    // How far along the track each photo's start edge is from the snap line.
    let target = null;
    slides.forEach((slide) => {
      const rect = slide.getBoundingClientRect();
      const distance = sign * ((rtl ? rect.right : rect.left) - snapLine);
      if (direction > 0 && distance > EDGE_TOLERANCE && (target === null || distance < target)) target = distance;
      if (direction < 0 && distance < -EDGE_TOLERANCE && (target === null || distance > target)) target = distance;
    });
    if (target === null) return;

    track.scrollBy({
      left: sign * target,
      behavior: this._reducedMotion.matches ? 'auto' : 'smooth'
    });
  }

  _scheduleUpdate() {
    if (this._frame) return;

    this._frame = requestAnimationFrame(() => {
      this._frame = 0;
      try {
        this._updateArrows();
      } catch (error) {
        report(error);
      }
    });
  }

  /* data-scrollable reveals the arrows (CSS). Each end disables its arrow.
   * No `scrollend` anywhere: Safari's support for it isn't settled, and the
   * rAF-throttled scroll listener already covers every position. */
  _updateArrows() {
    const track = this._track;
    const max = track.scrollWidth - track.clientWidth;
    const scrollable = max > EDGE_TOLERANCE;
    // RTL scroll positions run from 0 down to -max.
    const position = Math.abs(track.scrollLeft);

    this.toggleAttribute('data-scrollable', scrollable);
    this._setDisabled(this._prevButtons, !scrollable || position <= EDGE_TOLERANCE);
    this._setDisabled(this._nextButtons, !scrollable || position >= max - EDGE_TOLERANCE);
  }

  _setDisabled(buttons, disabled) {
    const value = String(disabled);
    buttons.forEach((button) => {
      if (button.getAttribute('aria-disabled') !== value) button.setAttribute('aria-disabled', value);
    });
  }
}

if (!window.customElements.get('a50-colorway-gallery')) {
  window.customElements.define('a50-colorway-gallery', A50ColorwayGallery);
}
